"use client";

import { useEffect } from "react";
import { getPageColorsFromRoute } from "@/lib/navigation";

interface PageColorProviderProps {
  page?: string;
  path?: string;
}

export function PageColorProvider({ page, path }: PageColorProviderProps) {
  useEffect(() => {
    const route = path || (page ? `/${page}` : undefined);
    if (!route) return;

    const pageColors = getPageColorsFromRoute(route);

    // Only update MiniNavBar specific CSS variables
    const root = document.documentElement;

    // Convert Tailwind color classes to CSS values for MiniNavBar
    const colorMap: Record<string, string> = {
      "blue-500": "hsl(221, 83%, 60%)",
      "blue-600": "hsl(221, 83%, 53%)",
      "blue-700": "hsl(221, 83%, 45%)",
      "rose-500": "hsl(346, 87%, 50%)",
      "rose-600": "hsl(346, 87%, 43%)",
      "rose-700": "hsl(346, 87%, 35%)",
      "green-600": "hsl(142, 76%, 36%)",
      "green-700": "hsl(142, 76%, 28%)",
      "indigo-500": "hsl(226, 70%, 55%)",
      "indigo-600": "hsl(238, 100%, 67%)",
      "indigo-700": "hsl(238, 100%, 59%)",
      "yellow-600": "hsl(45, 93%, 47%)",
      "yellow-700": "hsl(45, 93%, 39%)",
      "violet-500": "hsl(262, 87%, 65%)",
      "violet-600": "hsl(262, 100%, 66%)",
      "violet-700": "hsl(262, 100%, 58%)",
      "cyan-600": "hsl(199, 89%, 48%)",
      "cyan-700": "hsl(199, 89%, 40%)",
      "teal-500": "hsl(173, 70%, 45%)",
      "teal-600": "hsl(173, 58%, 39%)",
      "teal-700": "hsl(173, 58%, 31%)",
      "emerald-500": "hsl(152, 63%, 45%)",
      "emerald-600": "hsl(142, 76%, 36%)",
      "emerald-700": "hsl(142, 76%, 28%)",
      "gray-600": "hsl(220, 9%, 46%)",
      "gray-700": "hsl(220, 9%, 38%)",
      "purple-500": "hsl(275, 80%, 60%)",
      "purple-600": "hsl(262, 100%, 66%)",
      "purple-700": "hsl(262, 100%, 58%)",
      "orange-500": "hsl(24, 94%, 50%)",
      "orange-600": "hsl(24, 94%, 45%)",
      "amber-500": "hsl(45, 100%, 51%)",
      "amber-600": "hsl(45, 93%, 47%)",
      "sky-500": "hsl(199, 95%, 62%)",
      "sky-600": "hsl(199, 89%, 48%)",
      "slate-600": "hsl(215, 22%, 49%)",
      "slate-700": "hsl(215, 19%, 35%)",
    };

    // Extract color from Tailwind class (e.g., 'text-blue-600' -> 'blue-600')
    const extractColor = (className: string) => {
      const match = className.match(/(?:text-|bg-|border-)([a-z]+-\d+)/);
      return match ? match[1] : null;
    };

    // Update MiniNavBar and PageHero button colors - keep LandingTopBar and MegaMenu with fixed homepage colors
    const activeColor = extractColor(pageColors.nav.active);
    const hoverColor = extractColor(pageColors.nav.hover);
    const primaryButtonColor = extractColor(pageColors.button.primary);
    const secondaryButtonColor = extractColor(pageColors.button.secondary);

    if (activeColor && colorMap[activeColor]) {
      root.style.setProperty("--mininav-active-color", colorMap[activeColor]);
    }

    if (hoverColor && colorMap[hoverColor]) {
      root.style.setProperty("--mininav-hover-color", colorMap[hoverColor]);
    }

    // Update button colors for PageHero
    if (primaryButtonColor && colorMap[primaryButtonColor]) {
      root.style.setProperty(
        "--pagehero-primary-bg",
        colorMap[primaryButtonColor]
      );
    }

    if (secondaryButtonColor && colorMap[secondaryButtonColor]) {
      root.style.setProperty(
        "--pagehero-secondary-color",
        colorMap[secondaryButtonColor]
      );
    }
  }, [page, path]);

  return null;
}
